import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, throwError } from 'rxjs';
import {
  AdminDocumentsFacade,
  AdminDocumentUpdateDto,
  BulkUploadResult,
} from './admin-documents.facade';
import {
  AdminDocumentListItem,
  AdminDocumentUploadDto,
  AdminDocumentsFilter,
} from '@models/resources/admin-document.model';
import { environment } from '../../../../environments/environment';

/**
 * HTTP-Variante (Sprint 6 S6-16/S6-17 + Manueller-Test Bug 6 Polish).
 * Bindet die in Sprint 6 Tag 6 neu angelegten Admin-Endpoints
 * `POST /api/documents`, `PUT /api/documents/{id}`,
 * `DELETE /api/documents/{id}` + den Bug-6-Index-Endpoint
 * `GET /api/documents`.
 *
 * - `downloadBatch` bleibt Stub bis Sprint 7 (ZIP-Service).
 * - `toggleVisibility` macht GET+PUT-Roundtrip (Polish-Item 7).
 */
@Injectable()
export class HttpAdminDocumentsFacade implements AdminDocumentsFacade {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/documents`;

  list(filter?: AdminDocumentsFilter): Observable<AdminDocumentListItem[]> {
    let params = new HttpParams();
    if (filter?.classification && filter.classification !== 'all') {
      params = params.set('classification', filter.classification);
    }
    if (filter?.customer_id !== undefined) {
      params = params.set('customer_id', String(filter.customer_id));
    }
    if (filter?.search?.trim()) {
      params = params.set('search', filter.search.trim());
    }
    if (filter?.date_from) {
      params = params.set('date_from', filter.date_from);
    }
    if (filter?.date_to) {
      params = params.set('date_to', filter.date_to);
    }
    return this.http.get<AdminDocumentListItem[]>(this.base, { params });
  }

  bulkUpload(dtos: readonly AdminDocumentUploadDto[]): Observable<BulkUploadResult> {
    if (dtos.length === 0) {
      return of({ succeeded: [], failed: [] });
    }
    // Parallel-Uploads via forkJoin. Pro Upload-Stream catchError, damit
    // ein einzelner 422 nicht alle anderen kippt; das Ergebnis-Objekt
    // hält den Erfolgs/Failure-Status zusammen.
    const streams: Observable<
      | { kind: 'ok'; doc: AdminDocumentListItem }
      | { kind: 'err'; filename: string; reason: string }
    >[] = dtos.map((dto) =>
      this.upload(dto).pipe(
        map((doc) => ({ kind: 'ok' as const, doc })),
        catchError((err: { message?: string; status?: number }) =>
          of({
            kind: 'err' as const,
            filename: dto.name,
            reason: err?.status ? `HTTP ${err.status}` : (err?.message ?? 'unknown'),
          }),
        ),
      ),
    );
    return forkJoin(streams).pipe(
      map((results) => ({
        succeeded: results.filter((r) => r.kind === 'ok').map((r) => r.doc),
        failed: results
          .filter((r) => r.kind === 'err')
          .map((r) => ({ filename: r.filename, reason: r.reason })),
      })),
    );
  }

  upload(dto: AdminDocumentUploadDto): Observable<AdminDocumentListItem> {
    if (!dto.file) {
      return throwError(() => new Error('Multipart upload requires a File binary.'));
    }
    const form = new FormData();
    form.append('customer_id', String(dto.customer_id));
    form.append('type', dto.classification);
    form.append('title', dto.name);
    form.append('visible_to_customer', dto.visible_to_customer ? '1' : '0');
    form.append('file', dto.file, dto.file.name);

    return this.http
      .post<{ data: AdminDocumentListItem }>(this.base, form)
      .pipe(map((res) => res.data));
  }

  update(id: number, dto: AdminDocumentUpdateDto): Observable<AdminDocumentListItem> {
    // BE `UpdateDocumentRequest` validiert `title` / `type` /
    // `visible_to_customer` als `sometimes`. Wir mappen unser
    // FE-`classification` auf das BE-`type` 1:1.
    const body: Record<string, unknown> = {};
    if (dto.title !== undefined) body['title'] = dto.title;
    if (dto.classification !== undefined) body['type'] = dto.classification;
    if (dto.visible_to_customer !== undefined) {
      body['visible_to_customer'] = dto.visible_to_customer;
    }
    return this.http
      .put<{ data: AdminDocumentListItem }>(`${this.base}/${id}`, body)
      .pipe(map((res) => res.data));
  }

  /**
   * Toggle-Visibility ohne `current state`-Wissen: erst GET, dann PUT
   * mit invertiertem Flag. Two-Roundtrip-Cost ist vernachlässigbar, weil
   * die Action selten ist; alternative wäre ein dedizierter
   * `PATCH /api/documents/{id}/visibility`-Endpoint, der in Sprint 7
   * nachgezogen werden kann.
   */
  toggleVisibility(id: number): Observable<AdminDocumentListItem> {
    return this.http
      .get<{ data: AdminDocumentListItem }>(`${this.base}/${id}`)
      .pipe(
        switchMap((res) =>
          this.update(id, { visible_to_customer: !res.data.visible_to_customer }),
        ),
      );
  }

  delete(id: number): Observable<void> {
    return this.http
      .delete<unknown>(`${this.base}/${id}`)
      .pipe(map(() => undefined));
  }

  // B-16 (Sprint-7-Manual-Test): Einzel-Download für Admin. Vorher Stub,
  // Toast `ADMIN.STUB_ACTION_TOAST`. Endpoint existiert seit Sprint 5c
  // und ist staff-allow (Inline-Auth in `DocumentDownloadController`).
  download(id: number): Observable<Blob> {
    return this.http.get(`${this.base}/${id}/download`, {
      responseType: 'blob',
    });
  }

  // S7-24: ZIP-Batch-Download.
  downloadBatch(ids: readonly number[]): Observable<Blob> {
    return this.http.post(
      `${this.base}/batch-download`,
      { ids: [...ids] },
      { responseType: 'blob' },
    );
  }
}
